import apiClient from '../axios.js';

export const pointsService = {
  /**
   * 查詢會員點數餘額
   * @param {string} memberId - 會員 ID
   * @returns {Promise<Object>} 點數餘額資訊
   */
  getBalance: async (memberId) => {
    const response = await apiClient.get(`/points/balance/${memberId}`);
    return response;
  },

  /**
   * 查詢會員點數交易記錄
   * @param {string} memberId - 會員 ID
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁筆數
   * @param {string} params.transactionType - 交易類型 (earn, redeem, expire, adjust, refund, sign_in)
   * @param {string} params.status - 狀態 (pending, active, expired, used)
   * @param {string} params.startDate - 開始日期 (YYYY-MM-DD)
   * @param {string} params.endDate - 結束日期 (YYYY-MM-DD)
   * @returns {Promise<Object>} 交易記錄列表
   */
  getTransactions: async (memberId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.transactionType) queryParams.append('transactionType', params.transactionType);
    if (params.status) queryParams.append('status', params.status);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    const url = `/points/transactions/${memberId}${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response;
  },

  /**
   * 增加點數
   * @param {Object} data - 點數資料
   * @param {string} data.memberId - 會員 ID
   * @param {number} data.points - 點數數量
   * @param {string} data.reason - 說明
   * @param {string} data.orderId - 訂單 ID (選填)
   * @param {string} data.expiryDate - 過期日期 (選填, YYYY-MM-DD)
   * @returns {Promise<Object>} 交易記錄
   */
  addPoints: async (data) => {
    const response = await apiClient.post('/points/add', data);
    return response;
  },

  /**
   * 扣除點數
   * @param {Object} data - 點數資料
   * @param {string} data.memberId - 會員 ID
   * @param {number} data.points - 點數數量
   * @param {string} data.reason - 原因
   * @param {string} data.orderId - 訂單 ID (選填)
   * @returns {Promise<Object>} 交易記錄
   */
  deductPoints: async (data) => {
    const response = await apiClient.post('/points/deduct', data);
    return response;
  },

  /**
   * 查詢即將過期的點數
   * @param {string} memberId - 會員 ID
   * @param {number} days - 天數 (預設 7)
   * @returns {Promise<Object>} 即將過期的點數列表
   */
  getExpiringPoints: async (memberId, days = 7) => {
    const response = await apiClient.get(`/points/expiring/${memberId}?days=${days}`);
    return response;
  },

  /**
   * 查詢已過期的點數
   * @param {string} memberId - 會員 ID
   * @returns {Promise<Object>} 已過期的點數列表
   */
  getExpiredPoints: async (memberId) => {
    const response = await apiClient.get(`/points/expired/${memberId}`);
    return response;
  },

  /**
   * 查詢所有點數交易記錄（管理員用）
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 交易記錄列表
   */
  listAllTransactions: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.memberId) queryParams.append('memberId', params.memberId);
    if (params.transactionType) queryParams.append('transactionType', params.transactionType);
    if (params.status) queryParams.append('status', params.status);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.order) queryParams.append('order', params.order);

    const queryString = queryParams.toString();
    const url = `/admin/points/transactions${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response;
  },

  /**
   * 批次調整點數
   * @param {Object} data - 批次調整資料
   * @returns {Promise<Object>} 調整結果
   */
  batchAdjust: async (data) => {
    const response = await apiClient.post('/admin/points/batch-adjust', data);
    return response;
  },

  /**
   * 批次發放點數
   * @param {Object} data - 批次發放資料
   * @returns {Promise<Object>} 發放結果
   */
  batchIssue: async (data) => {
    const response = await apiClient.post('/admin/points/batch-issue', data);
    return response;
  },

  /**
   * 查詢調整歷史
   * @param {string} memberId - 會員 ID
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 調整歷史列表
   */
  getAdjustmentHistory: async (memberId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.order) queryParams.append('order', params.order);

    const queryString = queryParams.toString();
    const url = `/admin/points/adjustment-history/${memberId}${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response;
  },

  /**
   * 取得點數過期設定
   * @returns {Promise<Object>} 過期設定
   */
  getExpiryConfig: async () => {
    const response = await apiClient.get('/admin/points/expiry-config');
    return response;
  },

  /**
   * 更新點數過期設定
   * @param {Object} data - 設定資料
   * @returns {Promise<Object>} 更新後的設定
   */
  updateExpiryConfig: async (data) => {
    const response = await apiClient.put('/admin/points/expiry-config', data);
    return response;
  },

  /**
   * 查詢批次發放點數列表
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁數量
   * @param {string} params.status - 狀態
   * @param {string} params.search - 搜尋關鍵字
   * @returns {Promise<Object>} 批次發放點數列表
   */
  getBatchIssuances: async (params = {}) => {
    const queryParams = {
      ...params,
      type: 'points' // 只查詢點數發放記錄
    };
    const response = await apiClient.get('/admin/manual-issuance/history', { params: queryParams });
    return response;
  },

  /**
   * 查詢批次發放的點數交易明細
   * @param {string} issuanceId - 發放 ID
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 交易明細列表
   */
  getBatchIssueTransactions: async (issuanceId, params = {}) => {
    const response = await apiClient.get(`/admin/points/batch-issue/${issuanceId}/transactions`, { params });
    return response;
  },

  // 查詢批次發放明細（包含成功和失敗）
  getBatchIssueDetails: async (issuanceId, params = {}) => {
    const response = await apiClient.get(`/admin/points/batch-issue/${issuanceId}/details`, { params });
    return response;
  }
};
